import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeftRightIcon, CheckIcon, EyeOffIcon, PiggyBankIcon } from 'lucide-react';
import { useState } from 'react';
import { type InboxItem, useInboxActions } from '../../components/inbox/useInboxActions';
import { shortDayLabel } from '../../components/purchases/dates';
import { formatAmount } from '../../components/purchases/PurchaseRow';
import { SavingsPickerSheet } from '../../components/savings/SavingsPickerSheet';
import { CategoryPickerSheet } from '../../components/transactions/CategoryPickerSheet';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { usePageTitle } from '../../lib/usePageTitle';
import { cn } from '../../lib/utils';

export const Route = createFileRoute('/inbox/')({
  component: InboxPage,
});

function InboxPage() {
  usePageTitle('Att sortera');
  // Item stays set while the sheet animates closed so the title doesn't flash.
  const [pickerFor, setPickerFor] = useState<InboxItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingsFor, setSavingsFor] = useState<InboxItem | null>(null);
  const [savingsOpen, setSavingsOpen] = useState(false);
  const {
    inboxQuery,
    items,
    withSuggestion,
    bulkRunning,
    categorize,
    asSavings,
    asTransfer,
    exclude,
    acceptAll,
  } = useInboxActions();

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Att sortera</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Nya köp som behöver en kategori innan de räknas rätt.
          </p>
        </div>
        {withSuggestion.length > 1 && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => void acceptAll()}
            disabled={bulkRunning}
          >
            <CheckIcon aria-hidden />
            Godkänn alla förslag ({withSuggestion.length})
          </Button>
        )}
      </header>

      {inboxQuery.isLoading ? (
        <div className="divide-y divide-border rounded-2xl border border-border bg-card">
          {['a', 'b', 'c'].map((k) => (
            <div key={k} className="space-y-3 px-4 py-4">
              <div className="flex justify-between gap-3">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-16" />
              </div>
              <Skeleton className="h-8 w-40 rounded-full" />
            </div>
          ))}
        </div>
      ) : inboxQuery.isError ? (
        <p className="rounded-2xl border border-border bg-card p-6 text-sm text-negative">
          Kunde inte ladda listan: {inboxQuery.error.message}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card px-6 py-10 text-center">
          <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-positive-soft text-positive">
            <CheckIcon aria-hidden className="size-6" />
          </span>
          <p className="mt-4 text-[15px] font-semibold">Allt är sorterat</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Nya köp dyker upp här när banken synkas.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
          {items.map((item) => (
            <InboxRow
              key={item.id}
              item={item}
              disabled={bulkRunning}
              onAccept={() => {
                if (item.suggestion) void categorize(item, item.suggestion.categoryId, false);
              }}
              onPick={() => {
                setPickerFor(item);
                setPickerOpen(true);
              }}
              onSavings={() => {
                setSavingsFor(item);
                setSavingsOpen(true);
              }}
              onTransfer={() => void asTransfer(item)}
              onExclude={() => void exclude(item)}
            />
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <p className="text-center text-xs text-muted-foreground">
          Alla köp finns under{' '}
          <Link to="/transactions" className="underline underline-offset-2 hover:text-foreground">
            Köp
          </Link>
          .
        </p>
      )}

      <SavingsPickerSheet
        open={savingsOpen}
        onOpenChange={setSavingsOpen}
        title={savingsFor?.description}
        onSelect={(pot) => {
          if (savingsFor) void asSavings(savingsFor, pot);
        }}
      />

      <CategoryPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={pickerFor?.description ?? 'Välj kategori'}
        value={pickerFor?.suggestion?.categoryId ?? null}
        suggestions={
          pickerFor?.suggestion
            ? [
                {
                  categoryId: pickerFor.suggestion.categoryId,
                  label: pickerFor.suggestion.categoryName,
                },
              ]
            : []
        }
        // Empty pattern = no rule offered (incoming Swish is sorted by hand)
        showCreateRule={(pickerFor?.rulePattern.length ?? 0) > 0}
        onSelect={(categoryId, { createRule }) => {
          if (pickerFor) void categorize(pickerFor, categoryId, createRule);
        }}
      />
    </div>
  );
}

function InboxRow({
  item,
  disabled,
  onAccept,
  onPick,
  onSavings,
  onTransfer,
  onExclude,
}: {
  item: InboxItem;
  disabled: boolean;
  onAccept: () => void;
  onPick: () => void;
  onSavings: () => void;
  onTransfer: () => void;
  onExclude: () => void;
}) {
  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="line-clamp-2 text-[15px] leading-snug font-medium">{item.description}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {shortDayLabel(item.date)}
            {item.accountName ? ` · ${item.accountName}` : ''}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 text-[15px] leading-snug font-medium whitespace-nowrap tabular-nums',
            item.amount > 0 ? 'text-positive' : 'text-foreground',
          )}
        >
          {formatAmount(item.amount)}
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {item.suggestion && (
          <button
            type="button"
            onClick={onAccept}
            disabled={disabled}
            aria-label={`Sortera som ${item.suggestion.categoryName}`}
            className="inline-flex h-9 max-w-full min-w-0 items-center gap-1.5 rounded-full bg-primary/10 px-3.5 text-sm font-medium text-primary outline-none transition-colors hover:bg-primary/15 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 max-sm:h-10"
          >
            <CheckIcon aria-hidden className="size-3.5 shrink-0" />
            <span className="truncate">{item.suggestion.categoryName}</span>
          </button>
        )}
        <button
          type="button"
          onClick={onPick}
          disabled={disabled}
          className={cn(
            'inline-flex h-9 items-center rounded-full px-3.5 text-sm font-medium outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 max-sm:h-10',
            item.suggestion
              ? 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              : 'bg-primary text-primary-foreground hover:bg-primary/90',
          )}
        >
          {item.suggestion ? 'Annan kategori' : 'Välj kategori'}
        </button>
        {/* Phones: a labelled row of their own instead of icons orphaned on
            a wrapped line. From sm up: icon-only, trailing. */}
        <div className="-mx-2.5 -mb-1.5 grid w-[calc(100%+1.25rem)] grid-cols-3 gap-1 sm:mx-0 sm:flex sm:mb-0 sm:ml-auto sm:w-auto sm:gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground max-sm:min-w-0 max-sm:px-1 max-sm:text-[13px] sm:size-8 sm:rounded-lg sm:px-0 sm:[&_svg:not([class*='size-'])]:size-4"
            onClick={onSavings}
            disabled={disabled}
            aria-label="Markera som sparande"
            title="Markera som sparande"
          >
            <PiggyBankIcon aria-hidden />
            <span className="sm:sr-only">Sparande</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground max-sm:min-w-0 max-sm:px-1 max-sm:text-[13px] sm:size-8 sm:rounded-lg sm:px-0 sm:[&_svg:not([class*='size-'])]:size-4"
            onClick={onTransfer}
            disabled={disabled}
            aria-label="Markera som överföring mellan egna konton"
            title="Överföring mellan egna konton"
          >
            <ArrowLeftRightIcon aria-hidden />
            <span className="sm:sr-only">Överföring</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground max-sm:min-w-0 max-sm:px-1 max-sm:text-[13px] sm:size-8 sm:rounded-lg sm:px-0 sm:[&_svg:not([class*='size-'])]:size-4"
            onClick={onExclude}
            disabled={disabled}
            aria-label="Räkna inte med i budgeten"
            title="Räkna inte med i budgeten"
          >
            <EyeOffIcon aria-hidden />
            <span className="sm:sr-only">Räkna inte</span>
          </Button>
        </div>
      </div>
    </li>
  );
}
